import { DemoEnVivo } from '../components/DemoEnVivo.js';
import { Precios } from '../components/Precios.js';
import { ChartColumn, FileText, Layers, Palette, Route, Wifi } from 'lucide-react';

const ADMIN_URL = import.meta.env.VITE_ADMIN_URL ?? 'http://localhost:5173';

/**
 * Página pública.
 *
 * Aquí se convence o se pierde a alguien, y la gente no lee. Por eso el
 * argumento no se cuenta: se demuestra. La portada es el producto funcionando
 * —cambias el destino y el código no se mueve— y lo demás son frases de una
 * línea.
 *
 * La única parte que sí se lee con calma son los planes, así que es la que
 * más sitio y más cuidado tiene.
 */

/**
 * Lo que se lleva quien paga, con la frase entera.
 *
 * Antes eran ocho telegramas —"Cientos de golpe", "Carpetas y equipo"— y
 * fallaban por las dos puntas: unos no se entendian sin adivinar y otro
 * vendia algo que a un negocio pequeno le da igual, porque aqui no hay un
 * equipo que coordinar, hay una persona que imprime un cartel.
 *
 * Asi que son seis, y la segunda linea dice algo concreto que se puede
 * comprobar: el tope real del lote, el dato exacto que se mide, lo que pasa
 * de verdad si el contraste no da. Una frase vaga en este sitio no convence a
 * nadie y ademas nos obliga a cumplir lo que el lector haya imaginado.
 */
const CAPACIDADES = [
  {
    que: 'Tu carta, en un PDF',
    como: 'Súbelo y el código lo abre directo, sin página intermedia. Cambian los precios, cambias el archivo.',
    Icono: FileText,
  },
  {
    que: 'Todos tus códigos de una vez',
    como: 'Uno por mesa, por producto o por sede. Los creas en un solo paso y se bajan juntos en un ZIP.',
    Icono: Layers,
  },
  {
    que: 'Quién escanea, y desde dónde',
    como: 'Cuántos escaneos, de qué país, con qué teléfono y a qué hora del día.',
    Icono: ChartColumn,
  },
  {
    que: 'Con tu logo y tus colores',
    como: 'Y si el contraste queda tan bajo que el lector va a fallar, no te deja guardarlo.',
    Icono: Palette,
  },
  {
    que: 'Un código, varios destinos',
    como: 'El mismo código lleva a un sitio o a otro según el país, el idioma o la hora.',
    Icono: Route,
  },
  {
    que: 'Wifi y contacto sin teclear',
    como: 'Escanean y se conectan a tu red, o se guardan tu tarjeta en la agenda del teléfono.',
    Icono: Wifi,
  },
];

export function Landing() {
  return (
    <>
      {/*
        La cabecera y el heroe van sobre el mismo indigo, de borde a borde de
        la ventana, asi que comparten envoltorio. El contenido de dentro sigue
        en la misma columna que el resto de la pagina.
      */}
      <div className="portada">
        <header className="site-head">
          <a className="site-head__brand" href="/" aria-label="apuntaqr, inicio">
            {/* El logotipo es una mascara, no una imagen: asi se pinta del
                color que toque —lima aqui, grafito en el pie— con un solo
                archivo. */}
            <span className="logotipo" role="img" aria-label="Apunta QR" />
          </a>
          <nav aria-label="Principal">
            <a href="#precios">Precios</a>
            <a className="site-head__cta" href={`${ADMIN_URL}/acceso`}>
              Entrar
            </a>
          </nav>
        </header>

        <section className="hero">
          <div className="hero__texto">
            <p className="hero__eyebrow">Códigos QR dinámicos</p>
            <h1>
              {/* La palabra en lima, como en la pieza de marca. Solo aqui: es
                  el unico sitio con letra lo bastante grande para que la lima
                  sobre indigo cumpla de contraste. */}
              <span className="hero__marcado">Imprime</span> una vez.
              <br />
              Cambia cuando quieras.
            </h1>
            <p className="hero__lead">
              Actualiza el contenido de tu QR sin cambiar el código que ya tienes
              impreso. El cartel, la carta y la etiqueta siguen valiendo.
            </p>
            <div className="hero__actions">
              <a className="btn btn--primary" href={`${ADMIN_URL}/acceso`}>
                Crear mi primer código
              </a>
              <a className="btn btn--ghost" href="#precios">
                Ver precios
              </a>
            </div>
            <p className="hero__note">Gratis para empezar. Sin tarjeta.</p>
          </div>

          <DemoEnVivo />
        </section>
      </div>

      <main className="claro">
        <section className="tiras" aria-label="Lo que incluye">
          <ul>
            {CAPACIDADES.map(({ que, como, Icono }) => (
              <li key={que}>
                <span className="tiras__icono">
                  {/* El grosor por defecto de Lucide es 2, que a 19 px se ve
                      basto junto a un texto de 15. 1,6 lo empareja. */}
                  <Icono size={19} strokeWidth={1.6} aria-hidden />
                </span>
                <strong>{que}</strong>
                <span className="tiras__como">{como}</span>
              </li>
            ))}
          </ul>
          <p className="tiras__pie">
            Cada destino se revisa contra la lista de sitios peligrosos de Google
            antes de publicarse, y se vuelve a revisar mientras siga en pie.
          </p>
        </section>

        <Precios />
      </main>

      <footer className="site-foot">
        <span className="site-foot__brand">
          <span className="logotipo" role="img" aria-label="Apunta QR" />
        </span>
        <nav aria-label="Legal">
          <a href="/privacidad">Privacidad</a>
          <a href="/terminos">Términos</a>
          <a href="/reembolsos">Reembolsos</a>
        </nav>
      </footer>
    </>
  );
}
